import React, { useContext, useEffect, useState } from 'react';
import {
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Grid,
    MenuItem,
    Select,
    Tab,
    Tabs,
} from '@material-ui/core';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';

import { addressesApi } from 'api/addresses';
import { catalogApi } from 'api/catalog';
import { productionApi as api } from 'api/production';
import { warehouseApi } from 'api/warehouse';
import { UserContext } from 'contexts/UserContext';
import { DataTable, FormComponent, Notifications, TabPanel } from 'shared';

import { LineSlotMapUpload } from './lineSlotMapUpload';
import { SellableItemUpload } from './sellableItemUpload';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const UploadModal = function ({ closeModal, getData, packGroup, setNotification }) {
    const formFields = [
        { name: 'file', label: 'Sellable Item File', gridValue: 12, inputElement: 'file', accept: ['.csv'] },
    ];
    return (
        <Dialog open onClose={closeModal} fullWidth maxWidth="lg">
            <DialogTitle>Edit Sellable Items - Pack Group {packGroup.name}</DialogTitle>
            <DialogContent style={{ paddingTop: '0px' }}>
                <FormComponent
                    formFields={formFields}
                    onSubmit={async (formData) => {
                        const response = await api.uploadLineSlotMap({
                            file: formData.file,
                            packGroupID: packGroup.id,
                        });
                        if (response.status === true) {
                            setNotification({ text: 'Sellable items updated', level: 'success' });
                            closeModal();
                            getData({ packGroupID: packGroup.id });
                        } else {
                            let message = 'Error updating sellable items';
                            if (response) {
                                message += `: ${response.msg}`;
                            }
                            if (response.data && response.data.numItemsUpdated) {
                                message += `. Num items updated: ${response.data.numItemsUpdated}`;
                            }
                            setNotification({ text: message, level: 'error' });
                        }
                    }}
                />
                <br />
                <br />
            </DialogContent>
        </Dialog>
    );
};

const SellableItems = function () {
    const [year, setYear] = useState('');
    const [week, setWeek] = useState('');
    const [selectedTab, setSelectedTab] = useState('');
    const [packGroups, setPackGroups] = useState([]);
    const [lots, setLots] = useState([]);
    const [pos, setPos] = useState([]);
    const [upcomingWeek, setUpcomingWeek] = useState(false);
    const [uploadModalOpen, setUploadModalOpen] = useState(false);
    const [selectedSellableItemIDs, setSelectedSellableItemIDs] = useState([]);
    const [notification, setNotification] = useState({ text: null, level: null });
    const [loading, setLoading] = useState(false);
    const [taxonomy, setTaxonomy] = useState({ l1: [], l2: [] });
    const [countriesData, setCountriesData] = useState([]);
    const [countriesOfOriginForSellableItems, setCountriesOfOriginForSellableItems] = useState([]);
    const user = useContext(UserContext);

    const getData = async ({ newWeek, newYear, packGroupID }) => {
        setLoading(true);
        const existingPackGroups = packGroups;
        const packGroupsRes = await api.getPackGroups({
            facilityID: user.getFacilityID(),
            week: newWeek || week,
            year: newYear || year,
        });

        if (packGroupsRes.status === false) {
            return;
        }

        setWeek(packGroupsRes.data.week);
        setYear(packGroupsRes.data.year);
        setUpcomingWeek(packGroupsRes.data.upcomingWeek);

        const promises = packGroupsRes.data.packGroups.map(async (group) => {
            if (packGroupID && packGroupID !== group.id) {
                return existingPackGroups.find((gr) => gr.id === group.id);
            }
            const sellableItems = await api.getSellableItems(group.id);
            if (sellableItems.status === false) {
                group.sellableItems = [];
            } else {
                group.sellableItems = sellableItems.data.sellableItems.map((item) => {
                    if (item.countriesOfOrigin.length > 0) {
                        item.countriesOfOrigin = item.countriesOfOrigin.map((country) => ({
                            ...country,
                            text: `${country.countryName} (${country.countryCode})`,
                            value: country.countryID,
                        }));
                    }

                    if (item.pos.length > 0) {
                        item.pos = item.pos.map((po) => ({
                            ...po,
                            text: `PO ${po.id}: SKU ${po.sku} ${po.itemNameInternal}`,
                            value: po.poMapID,
                        }));
                    }
                    return item;
                });
            }

            // Find pricing status and append
            group.pricingStatus = 'none';
            const pricingStatusResp = await api.getAutoPricingStatus(group.id);
            if (pricingStatusResp.status !== false) {
                group.pricingStatus = pricingStatusResp.data.status;
            }

            const countriesOfOriginforSellableItemsResp = await api.getCountriesOfOriginForSellableItems();
            if (countriesOfOriginforSellableItemsResp === true) {
                setCountriesOfOriginForSellableItems(countriesOfOriginforSellableItemsResp.data.rows);
            }

            return group;
        });
        const packGroupsArr = await Promise.all(promises);

        setPackGroups(packGroupsArr);
        if (!selectedTab) {
            setSelectedTab(packGroupsArr[0] ? '0' : '');
        }
        setLoading(false);
    };

    const getLots = async () => {
        const lots = await warehouseApi.getLots(true);
        setLots(lots.data.rows || []);
    };

    const getPos = async () => {
        const pos = await api.getPlacedPOs();
        if (pos.status === true) {
            setPos(pos.data.items);
        }
    };

    const getTaxonomy = async () => {
        const promises = [1, 2].map((lvl) => catalogApi.getTaxonomyByLevel(lvl, 'AISLE'));
        const res = await Promise.all(promises);
        if (res.every((item) => item.status)) {
            setTaxonomy({
                l1: res[0].data.categories.map((item) => ({ text: item.name, value: item.id })),
                l2: res[1].data.categories.map((item) => ({ text: item.name, value: item.id })),
            });
        }
    };

    // Load countries
    const getCountries = async () => {
        const response = await addressesApi.getCountries();
        if (!response.status) {
            setNotification({ text: 'Failed to get countries', level: 'error' });
            return;
        }

        const countriesAsOpts = response.data.rows.map((row) => {
            row.text = `${row.name} (${row.countryCode})`;
            row.value = row.id;
            return row;
        });

        setCountriesData(countriesAsOpts);
    };

    useEffect(() => {
        getData({});
        getLots();
        getPos();
        getTaxonomy();
        getCountries();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const formFields = [
        {
            name: 'week',
            initialValue: week,
            inputElement: 'textField',
            gridValue: 5,
            label: 'Week',
            inputProps: { required: true, type: 'number', max: '53' },
        },
        {
            name: 'year',
            initialValue: year,
            inputElement: 'textField',
            gridValue: 5,
            label: 'Year',
            inputProps: { required: true, type: 'number', maxLength: '4' },
        },
    ];

    const handleChange = (event, value) => {
        setSelectedTab(value);
    };
    return (
        <div>
            <Notifications options={notification} />
            {uploadModalOpen === true && (
                <UploadModal
                    packGroup={packGroups[selectedTab]}
                    closeModal={() => {
                        setUploadModalOpen(false);
                    }}
                    getData={getData}
                    setNotification={setNotification}
                />
            )}
            <h2>
                Setup Sellable Items - Week {week}, {year}
            </h2>
            {week && year && (
                <FormComponent
                    formFields={formFields}
                    button={{ gridValue: 2, text: 'Change Week/Year' }}
                    onSubmit={async (formData) => {
                        await getData({ newWeek: formData.week, newYear: formData.year });
                    }}
                />
            )}
            <br />
            <br />
            {loading ? (
                <CircularProgress />
            ) : (
                <div>
                    {packGroups.length > 0 && selectedTab ? (
                        <div>
                            <Tabs
                                onChange={handleChange}
                                value={selectedTab}
                                indicatorColor="primary"
                                textColor="primary"
                                centered
                            >
                                {packGroups.map((group, index) => (
                                    <Tab key={group.id} label={`Pack Group ${group.name}`} value={`${index}`} />
                                ))}
                            </Tabs>
                            {packGroups.map((group, index) => {
                                const sellItems = group.sellableItems;
                                return (
                                    <TabPanel selectedTab={selectedTab} key={group.id} index={`${index}`}>
                                        <div>
                                            {sellItems && sellItems.length > 0 ? (
                                                <SellableItemsTable
                                                    packGroupID={group.id}
                                                    week={week}
                                                    year={year}
                                                    lots={lots.filter(
                                                        (lot) => lot.originFacilityID === group.facilityID,
                                                    )}
                                                    pos={pos.filter((po) => po.facilityID === group.facilityID)}
                                                    getData={getData}
                                                    loading={loading}
                                                    setUploadModalOpen={setUploadModalOpen}
                                                    sellableItemsData={sellItems}
                                                    upcomingWeek={upcomingWeek}
                                                    setNotification={setNotification}
                                                    setSelectedSellableItemIDs={setSelectedSellableItemIDs}
                                                    taxonomy={taxonomy}
                                                    pricingStatus={group.pricingStatus}
                                                    countriesData={countriesData}
                                                    countriesOfOriginForSellableItems={
                                                        countriesOfOriginForSellableItems
                                                    }
                                                    isThirdParty={group.isThirdParty}
                                                />
                                            ) : (
                                                <div>No sellable items found for this pack group.</div>
                                            )}
                                        </div>
                                    </TabPanel>
                                );
                            })}
                        </div>
                    ) : (
                        <div>No sellable item data found for this week.</div>
                    )}
                </div>
            )}
        </div>
    );
};

const SellableItemsTable = function ({
    countriesData,
    countriesOfOriginForSellableItems,
    getData,
    isThirdParty,
    loading,
    lots,
    packGroupID,
    pos,
    pricingStatus,
    sellableItemsData,
    setNotification,
    setSelectedSellableItemIDs,
    setUploadModalOpen,
    taxonomy,
    upcomingWeek,
    week,
    year,
}) {
    const [tableData, setTableData] = useState([]);
    const [uploadingItems, setUploadingItems] = useState(false);
    const [uploadingLineSlotMap, setUploadingLineSlotMap] = useState(false);
    const [showRemoveSellableItemsForm, setShowRemoveSellableItemsForm] = useState(false);
    const [removingSellableItems, setRemovingSellableItems] = useState(false);

    const onSaveEdit = async ({ field, id, value }) => {
        if (field === 'onSale') {
            value = value === true ? 'true' : 'false';
        }
        if (field === 'newItem') {
            value = value === true ? 'true' : 'false';
        }
        if (field === 'sampleItem') {
            value = value === true ? 'true' : 'false';
        }
        if (field === 'giftItem') {
            value = value === true ? 'true' : 'false';
        }
        if (field === 'exitSku') {
            value = value === true ? 'true' : 'false';
        }
        if (field === 'bestSeller') {
            value = value === true ? 'true' : 'false';
        }
        if (field === 'isBundle') {
            value = value === true ? 'true' : 'false';
        }
        if (field === 'isGwp') {
            value = value === true ? 'true' : 'false';
        }

        const res = await api.updateSellableItem(id, field, value);
        if (res.status !== true) {
            const msg = `Error updating sellable item ${res.msg ? `. ${res.msg}` : ''}`;
            setNotification({ text: msg, level: 'error' });
        }
        return res.status;
    };

    const removeLotFromSellableItem = async (sellableItemID, lotID) => {
        if (window.confirm(`Remove lot ${lotID} from sellable item?`) == true) {
            const res = await api.removeLotFromSellableItem(sellableItemID, lotID);
            if (res.status === true) {
                await getData({ packGroupID });
            } else {
                let msg = 'Error removing lot from sellable item';
                if (res.msg) {
                    msg += `. ${res.msg}`;
                }
                setNotification({ text: msg, level: 'error' });
            }
            return res;
        }
    };

    const removePOFromSellableItem = async (sellableItemID, poMapID, poItemName) => {
        if (window.confirm(`Remove PO item ${poItemName} from sellable item?`) === true) {
            const res = await api.removePOFromSellableItem(sellableItemID, poMapID);
            if (res.status === true) {
                await getData({ packGroupID });
            } else {
                let msg = 'Error removing PO from sellable item';
                if (res.msg) {
                    msg += `. ${res.msg}`;
                }
                setNotification({ text: msg, level: 'error' });
            }
            return res;
        }
    };

    const addLotToSellableItem = async (sellableItemID, lotID) => {
        const res = await api.updateSellableItem(sellableItemID, 'lotID', lotID);
        if (res.status === true) {
            await getData({ packGroupID });
            setNotification({ text: 'Lot added to sellable item', level: 'success' });
        } else {
            const msg = `Error updating sellable item ${res.msg ? `. ${res.msg}` : ''}`;
            setNotification({ text: msg, level: 'error' });
        }
        return res.status;
    };

    const addPOToSellableItem = async (sellableItemID, poMapID) => {
        const res = await api.updateSellableItem(sellableItemID, 'poMapID', poMapID);
        if (res.status === true) {
            await getData({ packGroupID });
            setNotification({ text: 'PO added to sellable item', level: 'success' });
        } else {
            const msg = `Error updating sellable item ${res.msg ? `. ${res.msg}` : ''}`;
            setNotification({ text: msg, level: 'error' });
        }
        return res.status;
    };

    const removeSellableItems = async (ids) => {
        setRemovingSellableItems(true);
        try {
            const res = await api.removeSellableItems(ids);
            if (res.status === false) {
                setNotification({ text: 'Error deleting sellable item(s)', level: 'error' });
            } else if (res.status === true) {
                if (res.data.error.length == 0) {
                    setNotification({
                        text: `${res.data.success.length}/${ids.length} items deleted`,
                        level: 'success',
                    });
                } else {
                    setNotification({
                        text: `Error deleting some sellable item(s) - ${res.data.success.length}/${
                            ids.length
                        } items deleted. \n\n Items with errors: ${JSON.stringify(res.data.error)}`,
                        level: 'warning',
                        autoHide: false,
                    });
                }
            }
            return res;
        } finally {
            setRemovingSellableItems(false);
        }
    };

    const markItemsForPricing = async () => {
        const res = await api.markPackGroupReadyForPricing(packGroupID);
        if (res.status === false) {
            setNotification({ text: `Error marking items for pricing: ${res.msg}`, level: 'error' });
        } else if (res.status === true) {
            setNotification({ text: 'Success', level: 'success' });
        }
    };

    const toolbarActions = [
        {
            name: 'Upload Line Slot Map',
            action: () => {
                setUploadingLineSlotMap(true);
            },
        },
        {
            name: 'Upload Sellable Items',
            action: () => {
                setUploadingItems(true);
            },
        },
        {
            name: 'Remove Sellable Items',
            action: () => {
                setShowRemoveSellableItemsForm(true);
            },
        },
    ];
    const bulkActions = [
        {
            name: 'Download Pre-Populated CSV',
            action: async (ids) => {
                const obj = {};
                obj.sellableItemIDs = ids;
                const res = await api.downloadPrePopulatedCsv(obj);
                if (!res || !res.status) {
                    setNotification({ text: `Failed download csv file: ${res.msg}`, level: 'error' });
                }
            },
        },
    ];

    if (upcomingWeek) {
        bulkActions.push({
            name: 'Delete',
            action: async (ids) => {
                await removeSellableItems(ids);
                await getData({});
                return true;
            },
        });

        // Only show pricing button if items are not ready to price
        if (pricingStatus == 'none') {
            toolbarActions.push({
                name: 'Mark items ready for pricing',
                action: () => {
                    markItemsForPricing();
                },
            });
        }
    }

    useEffect(() => {
        if (isThirdParty) {
            setTableData(
                (sellableItemsData || []).map((item) => ({
                    ...item,
                    name: item.itemNameInternal ?? item.name,
                })),
            );
        } else {
            setTableData(sellableItemsData);
        }
    }, [sellableItemsData, isThirdParty]);

    const rowStyle = (row) => {
        if (row.lots.length === 0 && row.pos.length === 0) {
            return {
                backgroundColor: '#ffdcdc',
            };
        }
        return {};
    };

    const isEditable = upcomingWeek === true;

    const columns = [
        { accessor: 'id', Header: 'ID' },
        { accessor: 'externalSku', Header: 'External Sku' },
        { accessor: 'name', Header: 'Name', width: 150, editable: isEditable },
        { accessor: 'portionName', Header: 'Portion Name', editable: isEditable },
        {
            accessor: 'price',
            Header: 'Price',
            editable: isEditable,
            editProps: { type: 'input', inputType: 'number', step: '.01', min: '0', max: '60', required: true },
        },
        {
            accessor: 'mischiefPick',
            Header: 'Portion Ct',
            editable: true,
            editProps: {
                type: 'input',
                inputType: 'number',
                integer: true,
                min: '1',
                max: '10',
                required: true,
            },
        },
        {
            accessor: 'madnessPick',
            Header: 'Madness',
            editable: true,
            editProps: {
                type: 'input',
                inputType: 'number',
                integer: true,
                min: '1',
                max: '12',
                required: true,
            },
        },
        {
            accessor: 'slot',
            Header: 'Line Slot',
            editable: true,
            editProps: {
                type: 'input',
                inputType: 'number',
                integer: true,
                required: true,
            },
        },
        {
            accessor: 'lots',
            Header: 'Lots',
            editable: true,
            filterValues: (row) => row.original.lots.map((item) => `Lot ${item.id}: ${item.name}`),
            editProps: {
                type: 'autocomplete-multiple-chips',
                options: lots.map((item) => ({ text: `Lot ${item.id}: ${item.itemName}`, value: item.id })),
                modalHeader: (row) => `Add lot to ${row.name}`,
                addButtonText: 'Add Lots',
                addItem: async ({ row, values }) => {
                    for (const value of values) {
                        const res = await addLotToSellableItem(row.id, value);
                        if (!res) {
                            return false;
                        }
                    }
                    return true;
                },
                removeItem: async ({ item, row }) => {
                    removeLotFromSellableItem(row.id, item.id);
                },
            },
        },
        {
            accessor: 'pos',
            Header: 'POs',
            editable: true,
            filterValues: (row) =>
                row.original.pos.map((item) => `PO ${item.id}: SKU ${item.sku} ${item.itemNameInternal}`),
            editProps: {
                type: 'autocomplete-multiple-chips',
                options: pos.map((item) => ({
                    text: `PO ${item.poID}: SKU ${item.sku} ${item.internalName}`,
                    value: item.poMapID,
                })),
                modalHeader: (row) => `Add PO to ${row.name}`,
                addButtonText: 'Add POs',
                addItem: async ({ row, values }) => {
                    for (const value of values) {
                        const res = await addPOToSellableItem(row.id, value);
                        if (!res) {
                            return false;
                        }
                    }
                    return true;
                },
                removeItem: async ({ item, row }) => {
                    removePOFromSellableItem(row.id, item.poMapID, item.name);
                },
            },
        },
        {
            accessor: 'maxQuantity',
            Header: 'Max Qty',
            editable: true,
            editProps: {
                required: true,
                type: 'input',
                inputType: 'number',
                integer: true,
            },
        },
        {
            accessor: 'onSale',
            Header: 'On Sale',
            type: 'checkbox',
            editable: isEditable,
            editProps: { type: 'checkbox' },
        },
        {
            accessor: 'isBundle',
            Header: 'Is Bundle',
            type: 'checkbox',
            editable: isEditable,
            editProps: { type: 'checkbox' },
        },
        { accessor: 'newItem', Header: 'New', type: 'checkbox', editable: isEditable, editProps: { type: 'checkbox' } },
        {
            accessor: 'hideSku',
            Header: 'Hide Sku',
            type: 'checkbox',
            editable: isEditable,
            editProps: { type: 'checkbox' },
        },
        {
            accessor: 'sampleItem',
            Header: 'Sample',
            type: 'checkbox',
            editable: isEditable,
            editProps: { type: 'checkbox' },
        },
        {
            accessor: 'soldOut',
            Header: 'Sold Out',
            type: 'checkbox',
            Cell: ({ row }) => {
                const [open, setOpen] = useState(false);
                const [reason, setReason] = useState();
                return (
                    <>
                        <Dialog fullWidth maxWidth="sm" open={open} onClose={() => setOpen(false)}>
                            <DialogContent>
                                <h3>Select Reason</h3>
                                <Select
                                    required
                                    style={{
                                        minWidth: 200,
                                    }}
                                    onChange={(e) => setReason(e.target.value)}
                                >
                                    {[
                                        'Late Truck',
                                        'Gapping',
                                        'Inventory Accuracy (Ops related)',
                                        'Inventory Accuracy (Planning related)',
                                        'Vendor Short - Case Short, Ct/Cs discrepancy',
                                        'Quality Issue - Rejection, QC pull',
                                        'No more inventory on hand',
                                        'Late Truck - DLVD',
                                        'Other',
                                    ].map((item, i) => (
                                        <MenuItem key={i} value={item}>
                                            {item}
                                        </MenuItem>
                                    ))}
                                </Select>
                                <br />
                                <br />
                                <Button
                                    variant="outlined"
                                    onClick={async () => {
                                        const res = await api.markItemSoldOut(row.original.id, reason, 'true');
                                        if (res.status === true) {
                                            await getData({ packGroupID });
                                            setNotification({ text: 'Item marked as Sold Out', level: 'success' });
                                        } else {
                                            setNotification({
                                                text: 'Could not mark item as Sold Out',
                                                level: 'error',
                                            });
                                        }
                                        setOpen(false);
                                    }}
                                >
                                    Confirm Reason
                                </Button>
                            </DialogContent>
                        </Dialog>
                        <Checkbox
                            checked={row.original.soldOut}
                            color="primary"
                            onChange={async (e) => {
                                if (row.original.soldOut === false) {
                                    e.preventDefault();
                                    setOpen(true);
                                } else {
                                    const res = await api.markItemSoldOut(row.original.id, '', 'false');
                                    if (res.status === true) {
                                        await getData({ packGroupID });
                                    }
                                }
                            }}
                        />
                    </>
                );
            },
        },
        {
            accessor: 'giftItem',
            Header: 'Gift',
            type: 'checkbox',
            editable: isEditable,
            editProps: { type: 'checkbox' },
        },
        {
            accessor: 'exitSku',
            Header: 'Exit Sku',
            type: 'checkbox',
            editable: isEditable,
            editProps: { type: 'checkbox' },
        },
        {
            accessor: 'bestSeller',
            Header: 'Best Seller',
            type: 'checkbox',
            editable: isEditable,
            editProps: { type: 'checkbox' },
        },
        {
            accessor: 'isGwp',
            Header: 'Gift With Purchase',
            type: 'checkbox',
            editable: isEditable,
            editProps: { type: 'checkbox' },
        },
        {
            accessor: 'allowBuffer',
            Header: 'Allow Buffer',
            type: 'checkbox',
            editable: isEditable,
            editProps: { type: 'checkbox' },
        },
        {
            accessor: 'soldOutThreshold',
            Header: 'Sold Out Threshold',
            editable: false,
            editProps: { type: 'input', inputType: 'number', integer: true },
        },
        {
            accessor: 'sourcingReason',
            Header: 'Sourcing Reason',
            editable: true,
        },
        {
            accessor: 'countriesOfOrigin',
            Header: 'Country of Origin',
            editable: true,
            filterValues: (row) =>
                countriesOfOriginForSellableItems
                    .filter((item) => item.sellableItemID === row.id)
                    .map((item) => `${item.countriesOfOrigin.countryName} (${item.countriesOfOrigin.countryCode})`),
            editProps: {
                type: 'autocomplete-multiple-chips',
                options: countriesData.map((country) => ({
                    text: `${country.name} (${country.countryCode})`,
                    value: country.id,
                })),
                modalHeader: 'Add Country of Origin to Item',
                addButtonText: 'Add Country',
                addItem: async ({ row, values }) => {
                    if (values.length > 3) {
                        setNotification({
                            text: 'Can only add up to 3 countries of origin at a time',
                            level: 'error',
                        });
                        return;
                    }

                    const res = await api.createSellableItemCountryOfOriginMap(row.id, 'countryOfOrigin', values);

                    if (res.status === true) {
                        await getData({ packGroupID });
                    } else {
                        setNotification({ text: res.msg, level: 'error' });
                        return false;
                    }

                    return true;
                },
                removeItem: async ({ item, row }) => {
                    const res = await api.removeCountryOfOriginFromSellableItem(row.id, item.countryID);
                    if (res.status === true) {
                        await getData({ packGroupID });
                    } else {
                        setNotification({ level: 'error', text: res.msg });
                        return false;
                    }
                    return true;
                },
            },
        },
        {
            accessor: 'level0AisleRank',
            Header: 'L0 Rank',
            editable: true,
            editProps: { type: 'input', inputType: 'number', integer: true },
        },
        {
            accessor: 'level1AisleRank',
            Header: 'L1 Rank',
            editable: true,
            editProps: { type: 'input', inputType: 'number', integer: true },
        },
        {
            accessor: 'level2AisleRank',
            Header: 'L2 Rank',
            editable: true,
            editProps: { type: 'input', inputType: 'number', integer: true },
        },
        {
            accessor: 'cartPickOOS',
            Header: 'Cart Pick Out Of Stock',
            type: 'checkbox',
            Cell: ({ row }) => {
                const [open, setOpen] = useState(false);
                const [reason, setReason] = useState();
                return (
                    <>
                        <Dialog fullWidth maxWidth="sm" open={open} onClose={() => setOpen(false)}>
                            <DialogContent>
                                <h3>Select Reason</h3>
                                <Select
                                    required
                                    style={{
                                        minWidth: 200,
                                    }}
                                    onChange={(e) => setReason(e.target.value)}
                                >
                                    {[
                                        'Vendor Short - Case Short, Ct/Cs discrepancy',
                                        'Inventory Accuracy (Ops related)',
                                        'Inventory Accuracy (Planning related)',
                                        'Gapping',
                                        'Late Truck',
                                        'Quality Issue - Rejection, QC pull',
                                        'No more inventory on hand',
                                        'Late Truck - DLVD',
                                        'Other',
                                    ].map((item, i) => (
                                        <MenuItem key={i} value={item}>
                                            {item}
                                        </MenuItem>
                                    ))}
                                </Select>
                                <br />
                                <br />
                                <Button
                                    variant="outlined"
                                    onClick={async () => {
                                        const res = await api.updateSellableItem(row.original.id, 'cartPickOOS', {
                                            reason,
                                            cartPickOOS: 'true',
                                        });
                                        if (res.status === true) {
                                            await getData({ packGroupID });
                                            setNotification({
                                                text: 'Item marked as Cart Pick Out of Stock',
                                                level: 'success',
                                            });
                                        } else {
                                            setNotification({
                                                text: 'Could not mark item as Cart Pick Out of Stock',
                                                level: 'error',
                                            });
                                        }
                                        setOpen(false);
                                    }}
                                >
                                    Confirm Reason
                                </Button>
                            </DialogContent>
                        </Dialog>
                        <Checkbox
                            disabled={row.original.slot !== null || row.original.externalSku >= 300000}
                            checked={row.original.cartPickOOS}
                            color="primary"
                            onChange={async (e) => {
                                if (row.original.cartPickOOS === false) {
                                    e.preventDefault();
                                    setOpen(true);
                                } else {
                                    const res = await api.updateSellableItem(row.original.id, 'cartPickOOS', {
                                        cartPickOOS: 'false',
                                    });
                                    if (res.status === true) {
                                        await getData({ packGroupID });
                                    }
                                }
                            }}
                        />
                    </>
                );
            },
        },
    ];

    if (isThirdParty) {
        columns.splice(2, 0, {
            accessor: 'thirdPartyItemID',
            Header: 'Partner SKU',
            Cell: ({ row }) => row.original.thirdPartyItemID,
        });
    }

    return (
        <div>
            {uploadingItems && (
                <Grid container>
                    <Grid item xs={11} />
                    <Grid item xs={2}>
                        <Button
                            color="secondary"
                            variant="contained"
                            onClick={() => {
                                setUploadingItems(false);
                            }}
                        >
                            <span>View Items</span>
                        </Button>
                    </Grid>
                    <Grid item xs={12}>
                        <SellableItemUpload
                            packGroupID={packGroupID}
                            onSuccess={() => {
                                getData({ packGroupID });
                                setUploadingItems(false);
                            }}
                        />
                    </Grid>
                </Grid>
            )}
            {uploadingLineSlotMap && (
                <Grid container>
                    <Grid item xs={11} />
                    <Grid item xs={1}>
                        <Button
                            color="secondary"
                            variant="contained"
                            onClick={() => {
                                setUploadingLineSlotMap(false);
                            }}
                        >
                            <span>View Items</span>
                        </Button>
                    </Grid>
                    <Grid item xs={12}>
                        <LineSlotMapUpload
                            packGroupID={packGroupID}
                            onSuccess={() => {
                                getData({ packGroupID });
                                setUploadingLineSlotMap(false);
                            }}
                        />
                    </Grid>
                </Grid>
            )}
            {showRemoveSellableItemsForm && (
                <Grid container style={{ marginBottom: '30px' }}>
                    <Grid container item justifyContent="flex-end">
                        <Button
                            color="secondary"
                            variant="contained"
                            onClick={() => {
                                setShowRemoveSellableItemsForm(false);
                            }}
                        >
                            <span>View Items</span>
                        </Button>
                    </Grid>
                    {removingSellableItems && (
                        <Grid item xs={12}>
                            <CircularProgress />
                        </Grid>
                    )}
                    <Grid item xs={12}>
                        <FormComponent
                            formFields={[
                                {
                                    name: 'sellableItemIDs',
                                    initialValue: '',
                                    inputElement: 'textField',
                                    gridValue: 10,
                                    label: 'Sellable Item IDs',
                                    inputProps: { required: true, multiline: true },
                                },
                            ]}
                            button={{ gridValue: 2, text: 'Remove' }}
                            onSubmit={async (formData) => {
                                const ids = formData.sellableItemIDs?.split(',');
                                const res = await removeSellableItems(ids, setNotification);
                                if (res.status === true) {
                                    getData({ packGroupID });
                                    setShowRemoveSellableItemsForm(false);
                                }
                            }}
                        />
                    </Grid>
                </Grid>
            )}
            {!uploadingItems && !uploadingLineSlotMap && (
                <DataTable
                    key={`${week}${year}`}
                    rowStyle={rowStyle}
                    columns={columns}
                    data={loading ? [] : tableData}
                    editable
                    saveEdit={onSaveEdit}
                    selectMultiple
                    title="Sellable Items"
                    bulkActions={bulkActions}
                    toolbarActions={toolbarActions}
                />
            )}
        </div>
    );
};

export { SellableItems };
